import type { Cell, EnemyKind, FruitKind } from '../sim/types';

export interface LevelPalette {
  background: number;
  floor: number;
  floorAlt: number;
  rim: number;
  accent: number;
  danger: number;
}

export interface EnemySpawn {
  kind: EnemyKind;
  cell: Cell;
}

export interface ParsedLevel {
  width: number;
  height: number;
  permanentWalls: Set<string>;
  normalWalls: Set<string>;
  overdriveWalls: Set<string>;
  breakables: Set<string>;
  cores: Cell[];
  exit: Cell;
  playerSpawns: [Cell, Cell];
  enemies: EnemySpawn[];
  shard: Cell | null;
  conveyors: Map<string, 'up' | 'down' | 'left' | 'right'>;
  gates: Set<string>;
  shiftPads: Set<string>;
  lasers: Set<string>;
  fadingFloors: Set<string>;
  iceFloors: Set<string>;
  snowFloors: Set<string>;
  boss: Cell | null;
  /** Door back to the previous room, or null in the first room. */
  backDoor: Cell | null;
  /** Doorways between rooms of the connected world, left to right. */
  doors: Cell[];
}

export interface LevelDefinition {
  id: number;
  name: string;
  kicker: string;
  objective: string;
  targetTimeMs: number;
  automaticShiftMs: number | null;
  extractionMs: number;
  ranks: { B: number; A: number; S: number };
  palette: LevelPalette;
  fruitOrder: readonly FruitKind[];
  map: readonly string[];
  boss: boolean;
}

// Bright daylight snow palettes. `background` is the sky, `floor`/`floorAlt`
// are the two snow tones of the arena checker, `rim` tints the ice blocks and
// the arena frame, and `accent`/`danger` drive props and hazards.
const PALETTES = {
  garden: { background: 0x8ad9f7, floor: 0xf4fcff, floorAlt: 0xdcf0fb, rim: 0x8fd3f0, accent: 0xffd34e, danger: 0xff5f74 },
  circuit: { background: 0xffc2da, floor: 0xfff5f9, floorAlt: 0xffe3ee, rim: 0xffa8c8, accent: 0xffd166, danger: 0xff4778 },
  dream: { background: 0xa6e8dd, floor: 0xf1fffb, floorAlt: 0xd8f6ee, rim: 0x86d9c6, accent: 0x59d0ff, danger: 0xff7f9b },
  vault: { background: 0x9fcbec, floor: 0xeff8ff, floorAlt: 0xd8e9f8, rim: 0x7fb4dd, accent: 0xbff062, danger: 0xff527c },
  warden: { background: 0xbcb1f2, floor: 0xf7f3ff, floorAlt: 0xe6dffb, rim: 0xa593e8, accent: 0x6ef0d2, danger: 0xff658d },
  blizzard: { background: 0x8fd2f2, floor: 0xf7fcff, floorAlt: 0xdfeffb, rim: 0x86c3e8, accent: 0xff8fb0, danger: 0xffb347 },
  apple: { background: 0xaae59c, floor: 0xf5fff0, floorAlt: 0xddf6d3, rim: 0x8ecf7e, accent: 0xc8f45d, danger: 0xff7a6d },
  cocoa: { background: 0xe6bd93, floor: 0xfff5e8, floorAlt: 0xf4e2cb, rim: 0xd0a071, accent: 0xffb561, danger: 0xff5f7c },
  aurora: { background: 0x93c7f7, floor: 0xf2f8ff, floorAlt: 0xdcebfd, rim: 0x86b5ea, accent: 0xd7a4ff, danger: 0xff6f9c },
  crown: { background: 0xbccdf5, floor: 0xf8faff, floorAlt: 0xe5ebfc, rim: 0xa1b4e8, accent: 0xffe07a, danger: 0xff5e89 },
} satisfies Record<string, LevelPalette>;

export const LEVELS: readonly LevelDefinition[] = [
  {
    id: 1,
    name: 'FIRST FLURRY',
    kicker: 'Vanilla Snowgarden',
    objective: 'Harvest two batches and break the Cherry Scoop\'s line of sight.',
    targetTimeMs: 90_000,
    automaticShiftMs: null,
    extractionMs: 18_000,
    ranks: { B: 5_200, A: 7_200, S: 9_400 },
    palette: PALETTES.garden,
    fruitOrder: ['strawberry', 'greenApple'],
    boss: false,
    map: [
      '###############',
      '#P...C.......E#',
      '#.###.....###.#',
      '#...C..X......#',
      '#.#.#.###.#.#.#',
      '#............C#',
      '#.#.###.###.#.#',
      '#.............#',
      '#.###.....###.#',
      '#2...C........#',
      '###############',
    ],
  },
  {
    id: 2,
    name: 'SYRUP STATION',
    kicker: 'Strawberry Switchyard',
    objective: 'Ride the gusts, bait the Rammer, and find the Sugar Shard.',
    targetTimeMs: 120_000,
    automaticShiftMs: 28_000,
    extractionMs: 16_000,
    ranks: { B: 8_000, A: 11_000, S: 14_500 },
    palette: PALETTES.circuit,
    fruitOrder: ['orange', 'banana', 'cherries'],
    boss: false,
    map: [
      '###############',
      '#K..C.#>.....E#',
      '#.#.....#.##..#',
      '#P#.C.g..#C..R#',
      '#...###.#...#.#',
      '#.....H.#b...C#',
      '###.#..T#.#...#',
      '#..C.>>>..#...#',
      '#.#...#.###.#.#',
      '#2..C..b......#',
      '###############',
    ],
  },
  {
    id: 3,
    name: 'MIRROR MINT',
    kicker: 'Two-State Ice Palace',
    objective: 'Plan one maze ahead. Your Frostwall becomes their rail.',
    targetTimeMs: 150_000,
    automaticShiftMs: 20_000,
    extractionMs: 15_000,
    ranks: { B: 10_000, A: 14_000, S: 18_000 },
    palette: PALETTES.dream,
    fruitOrder: ['blueberries', 'pear', 'grapes'],
    boss: false,
    map: [
      '###############',
      '#K..C.n#.....E#',
      '#.#o..#.#o.#..#',
      '#P#..C...#C..W#',
      '#...#.#.#...#.#',
      '#.n....#o...C.#',
      '##..#..p..#...#',
      '#..C.o#..n....#',
      '#.#..###...##.#',
      '#2...C........#',
      '###############',
    ],
  },
  {
    id: 4,
    name: 'COLD STORAGE',
    kicker: 'Chocolate Freezer Vault',
    objective: 'Read the marks, cross the heat sweeps, escape the lockdown.',
    targetTimeMs: 180_000,
    automaticShiftMs: 17_000,
    extractionMs: 14_000,
    ranks: { B: 13_000, A: 17_500, S: 23_000 },
    palette: PALETTES.vault,
    fruitOrder: ['pineapple', 'greenApple', 'watermelon', 'cherries'],
    boss: false,
    map: [
      '###############',
      '#K.C#..l..C..E#',
      '#...#.#...#.#.#',
      '#P.C..d#....S.#',
      '##.#...#.#....#',
      '#C..#T...W#.C.#',
      '#...#.d#..#...#',
      '#..C.l..#.....#',
      '#.#..##...##..#',
      '#2.C...#..C...#',
      '###############',
    ],
  },
  {
    id: 5,
    name: "WARDEN'S SNOWGLOBE",
    kicker: 'Fractured Winter Ring',
    objective: 'Break the globe: shield fruit, redirects, energized shatters.',
    targetTimeMs: 180_000,
    automaticShiftMs: 12_000,
    extractionMs: 12_000,
    ranks: { B: 16_000, A: 22_000, S: 29_000 },
    palette: PALETTES.warden,
    fruitOrder: ['grapes', 'strawberry', 'pineapple'],
    boss: true,
    map: [
      '###############',
      '#K...C#......E#',
      '#..#...#...#..#',
      '#P.C..B...C..W#',
      '#.#..##.##..#.#',
      '#...p.....p.C.#',
      '##...#...#....#',
      '#..C...S...#..#',
      '#.#.###...#.#.#',
      '#2....C.......#',
      '###############',
    ],
  },
  {
    id: 6,
    name: 'BERRY BLIZZARD',
    kicker: 'Strawberry Whiteout',
    objective: 'Cross deep snow, ride crosswinds, and keep the fruit chain alive.',
    targetTimeMs: 150_000,
    automaticShiftMs: 23_000,
    extractionMs: 15_000,
    ranks: { B: 12_000, A: 16_000, S: 21_000 },
    palette: PALETTES.blizzard,
    fruitOrder: ['blueberries', 'strawberry', 'watermelon'],
    boss: false,
    map: [
      '###############',
      '#K.C..#>.....E#',
      '#..#zzz..##...#',
      '#P.C#..z..C.R.#',
      '#.#...##.#..#.#',
      '#...H.z.#...C.#',
      '##.#..T...#...#',
      '#..C.zz.#.....#',
      '#...##...#..#.#',
      '#2...C........#',
      '###############',
    ],
  },
  {
    id: 7,
    name: 'APPLE AVALANCHE',
    kicker: 'Green Apple Glacier',
    objective: 'Buffer turns across polished ice before the Seer cuts you off.',
    targetTimeMs: 165_000,
    automaticShiftMs: 19_000,
    extractionMs: 14_000,
    ranks: { B: 13_500, A: 18_000, S: 23_500 },
    palette: PALETTES.apple,
    fruitOrder: ['greenApple', 'pear', 'banana'],
    boss: false,
    map: [
      '###############',
      '#K..C#..i....E#',
      '#...#iii.#..#.#',
      '#P.C..i#..C.S.#',
      '##..#...#.#...#',
      '#..p...i#...C.#',
      '#.#..T.#..##..#',
      '#..C.ii...#...#',
      '#...#..##...#.#',
      '#2....C.......#',
      '###############',
    ],
  },
  {
    id: 8,
    name: 'COCOA CAVERNS',
    kicker: 'Molten Chocolate Grotto',
    objective: 'Crack cocoa crust, dodge heat sweeps, and shatter the rush.',
    targetTimeMs: 175_000,
    automaticShiftMs: 18_000,
    extractionMs: 14_000,
    ranks: { B: 15_000, A: 20_000, S: 26_000 },
    palette: PALETTES.cocoa,
    fruitOrder: ['orange', 'pineapple', 'cherries', 'watermelon'],
    boss: false,
    map: [
      '###############',
      '#K.C#..b..C..E#',
      '#..#d...#d.#..#',
      '#P.C..l#....R.#',
      '##..#...##..#.#',
      '#C.b.W....#.C.#',
      '#...#.d#..#...#',
      '#..C..b.#.....#',
      '#.#..##...#...#',
      '#2.C...#..C...#',
      '###############',
    ],
  },
  {
    id: 9,
    name: 'AURORA SWITCHBACK',
    kicker: 'Northern-Light Causeway',
    objective: 'Switch states, read every hunter, and carve a route through ice.',
    targetTimeMs: 180_000,
    automaticShiftMs: 15_000,
    extractionMs: 13_000,
    ranks: { B: 17_000, A: 23_000, S: 30_000 },
    palette: PALETTES.aurora,
    fruitOrder: ['blueberries', 'grapes', 'pear', 'strawberry'],
    boss: false,
    map: [
      '###############',
      '#K.C..n#..C..E#',
      '#..#i..i#..#..#',
      '#P.C#.o....W..#',
      '##...#.#.#...##',
      '#C..S...#l..C.#',
      '#.#..p.#..#...#',
      '#..C.#T.n....C#',
      '#...##..#..##.#',
      '#2..C.........#',
      '###############',
    ],
  },
  {
    id: 10,
    name: 'CROWN OF WINTER',
    kicker: 'Aurora Throne Snowglobe',
    objective: 'Master snow, ice, rails, and shatters to dethrone the Frost Warden.',
    targetTimeMs: 190_000,
    automaticShiftMs: 10_000,
    extractionMs: 12_000,
    ranks: { B: 20_000, A: 27_000, S: 35_000 },
    palette: PALETTES.crown,
    fruitOrder: ['pineapple', 'greenApple', 'banana'],
    boss: true,
    map: [
      '###############',
      '#K.C#..n.....E#',
      '#...#z.z#..#..#',
      '#P.C.Wi#..C...#',
      '##.#...#.#....#',
      '#..B..#o...C..#',
      '#...#..p#..#..#',
      '#..C...S..#...#',
      '#.#..##...##..#',
      '#2....C.......#',
      '###############',
    ],
  },
] as const;

function key(x: number, y: number): string {
  return `${x},${y}`;
}

export function parseLevel(definition: LevelDefinition): ParsedLevel {
  const height = definition.map.length;
  const width = definition.map[0]?.length ?? 0;
  const permanentWalls = new Set<string>();
  const normalWalls = new Set<string>();
  const overdriveWalls = new Set<string>();
  const breakables = new Set<string>();
  const cores: Cell[] = [];
  const enemies: EnemySpawn[] = [];
  const conveyors = new Map<string, 'up' | 'down' | 'left' | 'right'>();
  const gates = new Set<string>();
  const shiftPads = new Set<string>();
  const lasers = new Set<string>();
  const fadingFloors = new Set<string>();
  const iceFloors = new Set<string>();
  const snowFloors = new Set<string>();
  let exit: Cell | null = null;
  let playerOne: Cell | null = null;
  let playerTwo: Cell | null = null;
  let shard: Cell | null = null;
  let boss: Cell | null = null;
  let backDoor: Cell | null = null;
  const doors: Cell[] = [];

  definition.map.forEach((row, y) => {
    if (row.length !== width) throw new Error(`Level ${definition.id} row ${y} has inconsistent width.`);
    [...row].forEach((tile, x) => {
      const cell = { x, y };
      const cellId = key(x, y);
      switch (tile) {
        case '#': permanentWalls.add(cellId); break;
        case 'n': normalWalls.add(cellId); break;
        case 'o': overdriveWalls.add(cellId); break;
        case 'b': breakables.add(cellId); break;
        case 'C': cores.push(cell); break;
        case 'E': exit = cell; break;
        case 'P': playerOne = cell; break;
        case '2': playerTwo = cell; break;
        case 'H': shard = cell; break;
        case 'K': backDoor = cell; break;
        case 'A': doors.push(cell); break;
        case 'T': enemies.push({ kind: 'tracer', cell }); break;
        case 'R': enemies.push({ kind: 'rammer', cell }); break;
        case 'S': enemies.push({ kind: 'seer', cell }); break;
        case 'W': enemies.push({ kind: 'wisp', cell }); break;
        case 'X': enemies.push({ kind: 'scoop', cell }); break;
        case '>': conveyors.set(cellId, 'right'); break;
        case '<': conveyors.set(cellId, 'left'); break;
        case '^': conveyors.set(cellId, 'up'); break;
        case 'v': conveyors.set(cellId, 'down'); break;
        case 'g': gates.add(cellId); break;
        case 'p': shiftPads.add(cellId); break;
        case 'l': lasers.add(cellId); break;
        case 'd': fadingFloors.add(cellId); break;
        case 'i': iceFloors.add(cellId); break;
        case 'z': snowFloors.add(cellId); break;
        case 'B': boss = cell; break;
        default: break;
      }
    });
  });

  if (!exit || !playerOne) throw new Error(`Level ${definition.id} requires an exit and P1 spawn.`);
  return {
    width,
    height,
    permanentWalls,
    normalWalls,
    overdriveWalls,
    breakables,
    cores,
    exit,
    playerSpawns: [playerOne, playerTwo ?? playerOne],
    enemies,
    shard,
    conveyors,
    gates,
    shiftPads,
    lasers,
    fadingFloors,
    iceFloors,
    snowFloors,
    boss,
    backDoor,
    doors,
  };
}

export function getLevel(id: number): LevelDefinition {
  const level = LEVELS.find((candidate) => candidate.id === id);
  if (!level) throw new Error(`Unknown level ${id}.`);
  return level;
}
